import { CSSProperties, FC, useEffect, useState } from 'react';
import { PrismicRichText } from '@prismicio/react';
import { useFilteredSliceItemsByLocationSlug, useIsMobile } from '@/hooks';
import {
  Container,
  Heading,
  Link,
  Placeholder,
  PrismicImageWithAlt,
  Slider,
} from '@/components';
import { CategoriesSliceDefault, CategoriesSliceGrid } from '@/types';
import { getSlicePadding, htmlSerializerWithProps } from '@/utils';
import * as prismicH from '@prismicio/helpers';
import classNames from 'classnames';
import styles from './categories.module.scss';

const CategoriesCarousel: FC<CategoriesSliceDefault> = ({ items, primary }) => {
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(items);
  const { isMobile } = useIsMobile(1024);
  const [isLoading, setIsLoading] = useState(true);
  const {
    spacing_between,
    spacing_between_mobile,
    image_size,
    image_size_mobile,
    link_text_see_all,
    link_text_color_see_all,
    header_color,
    categories_header,
    category_name_color,
    bottom_spacing,
    background_color,
    border_radius,
    round_image,
    full_width,
  } = primary;
  const borderRadius = round_image
    ? '100%'
    : border_radius
    ? `${border_radius}px`
    : '0px';
  const paddingTop = background_color ? '2.5rem' : '0px';
  const paddingSection = primary?.padding_section;
  const background = background_color ?? false;
  const backgroundImage = primary?.background_image?.url;
  const backgroundStyleFixed = primary?.background_style_fixed_cover;
  const minHeight = primary?.min_heigth;

  const activeAutoplay = !!primary?.autoplay;
  const autoplayConfig = {
    delay: 5000,
    disableOnInteraction: false,
    stopOnLastSlide: false,
  };
  const categoriesHeaderText = prismicH.asText(categories_header);
  const ariaLabel = categoriesHeaderText
    ? `${categoriesHeaderText} carousel`
    : 'Categories carousel';

  const title = prismicH.asText(categories_header);

  // Calculate slices per view based on screen width and items per view
  // If itemsPerView is not a number (undefined or "auto") or width is less than 1024 (mobile), use 'auto'
  // Otherwise, convert itemsPerView to a number (options are "auto", "2", "3", "4", "5")
  const itemsPerView = primary?.items_per_view;
  const slicesPerView =
    !itemsPerView || isMobile ? 'auto' : Number(itemsPerView);

  // Determine content alignment based on primary content_align value
  // Default to 'center' if not specified
  const itemsAlign = primary?.content_align;
  const contentAlign =
    itemsAlign === 'left'
      ? 'flex-start'
      : itemsAlign === 'right'
      ? 'flex-end'
      : 'center';

  useEffect(() => {
    if (filteredItems?.length) {
      setIsLoading(false);
    }
  }, [filteredItems]);

  const getImageSize = () => {
    if (isMobile) {
      return image_size_mobile ? `${image_size_mobile}px` : undefined;
    }

    return `${image_size ?? 280}px`;
  };

  const getSpaceBetween = () => {
    if (isMobile) {
      return spacing_between_mobile;
    }

    return spacing_between;
  };

  return (
    <section
      aria-label={ariaLabel}
      className={classNames(
        styles.categories__carousel_section,
        getSlicePadding(paddingSection),
        {
          [styles.spacing_bottom]: bottom_spacing,
          [styles.padding_background]: paddingTop && background,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={{
        background: background_color!,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <Slider
        activatedHover={false}
        activeAutoplay={activeAutoplay}
        ariaLabel={ariaLabel}
        autoplay={activeAutoplay ? autoplayConfig : undefined}
        className={styles.categories_carousel}
        freeMode
        isNotPaddingMobile={full_width}
        loop={activeAutoplay}
        seeAllElement={
          <Link
            className={styles['collections__see-all']}
            href="/shop"
            style={{ color: link_text_color_see_all ?? '' }}
          >
            {link_text_see_all || 'SHOP ALL'}
          </Link>
        }
        showSlidesOutsideContainer={full_width}
        sliderHeading={
          title ? (
            <Heading level={2} style={{ color: header_color ?? '' }}>
              {categoriesHeaderText}
            </Heading>
          ) : undefined
        }
        slidesPerView={slicesPerView}
        spaceBetween={getSpaceBetween() ?? 24}
      >
        {isLoading || !items?.length
          ? Array.from({ length: 30 }).map((_, index) => (
              <div
                className={styles.categories_carousel__placeholder_category}
                key={index}
              >
                <Placeholder
                  className={styles.categories_carousel__placeholder_image}
                />
                <Placeholder
                  className={styles.categories_carousel__placeholder_title}
                />
              </div>
            ))
          : filteredItems?.map(category => (
              <Link
                className={styles.slide__main}
                href={category?.link ?? ''}
                key={category.category_name}
                style={{
                  minWidth: getImageSize(),
                  alignItems: contentAlign,
                }}
              >
                {category.category_name && (
                  <p
                    className={styles.categories_carousel__category_name}
                    style={{ color: category_name_color ?? '' }}
                  >
                    {category.category_name}
                  </p>
                )}
                <figure
                  className={classNames(styles.category_image, {
                    [styles.category_image_placeholder]:
                      !category?.category_image?.url,
                  })}
                  style={{ borderRadius, maxWidth: getImageSize() }}
                >
                  <PrismicImageWithAlt
                    defaultAlt="Category image"
                    field={category.category_image}
                    showPlaceholderIfEmpty
                    sizes="(min-width: 1200px) 158px, 
                                        (min-width: 992px) 170px, 
                                        (min-width: 768px) 157px, 
                                        (min-width: 576px) 156px, 50vw"
                    style={{
                      borderRadius,
                      objectFit: 'cover',
                    }}
                    title={category?.category_name ?? ''}
                  />
                </figure>
              </Link>
            ))}
      </Slider>
    </section>
  );
};

const CategoriesGrid: FC<CategoriesSliceGrid> = ({ primary, items }) => {
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(items);
  const [hovered, setHovered] = useState<boolean>();
  const { isMobile } = useIsMobile(1024);

  const {
    categories_header,
    bottom_spacing,
    background_color,
    border_radius,
    round_image,
    header_color,
    category_name_color,
    cta_text_color,
    cta_text_color_hover,
    cta_background_color,
    cta_background_color_hover,
    cta_border,
    cta_border_hover,
    hide_cta,
  } = primary;
  const borderRadius = round_image
    ? '100%'
    : border_radius
    ? `${border_radius}px`
    : '0px';
  const paddingSection = primary?.padding_section;
  const paddingTop = !!background_color;
  const background = background_color ?? false;
  const backgroundImage = primary?.background_image?.url;
  const backgroundStyleFixed = primary?.background_style_fixed_cover;
  const minHeight = primary?.min_heigth;
  const maxWidth = primary?.items_max_width;
  const maxWidthMobile = primary?.items_max_width_mobile;
  const contentAlign = primary?.content_align;

  // Determine number of columns per row
  const columnsPerRow = primary?.columns_per_row;
  const categoriesHeaderText = prismicH.asText(categories_header);
  const ariaLabel = categoriesHeaderText
    ? `${categoriesHeaderText} carousel`
    : 'Categories carousel';

  const getItemMaxWidth = () => {
    if (isMobile) {
      return maxWidthMobile ? `${maxWidthMobile}px` : undefined;
    }

    return `${maxWidth ?? 280}px`;
  };

  return (
    <section
      aria-label={ariaLabel}
      className={classNames(
        styles.categories__carousel_section,
        getSlicePadding(paddingSection),
        {
          [styles.spacing_bottom]: bottom_spacing,
          [styles.padding_background]: paddingTop && background,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={{
        background: background_color!,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <Container className={styles.categories_grid}>
        <PrismicRichText
          components={htmlSerializerWithProps({ color: header_color })}
          field={categories_header}
        />
        <div
          className={styles.categories_grid__container}
          style={
            {
              '--columns-per-row': columnsPerRow,
              justifyItems: contentAlign,
            } as CSSProperties
          }
        >
          {filteredItems?.map(category => {
            const cardContent = (
              <>
                {category.category_name && (
                  <p style={{ color: category_name_color ?? '' }}>
                    {category.category_name}
                  </p>
                )}
                <figure
                  className={classNames(styles.image, {
                    [styles.image_placeholder]: !category?.category_image?.url,
                  })}
                  style={{ borderRadius }}
                >
                  <PrismicImageWithAlt
                    defaultAlt="Category image"
                    field={category.category_image}
                    fill
                    showPlaceholderIfEmpty
                    sizes="(min-width: 1200px) 158px, 
                                        (min-width: 992px) 170px, 
                                        (min-width: 768px) 157px, 
                                        (min-width: 576px) 156px, 50vw"
                    style={{ borderRadius, objectFit: 'contain' }}
                    title={category?.category_name ?? ''}
                  />
                </figure>
                {!hide_cta && (
                  <span
                    onClick={() => setHovered(true)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setHovered(true);
                      }
                    }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    role="button"
                    style={{
                      backgroundColor: (hovered
                        ? cta_background_color_hover
                        : cta_background_color) as string,
                      color: (hovered
                        ? cta_text_color_hover
                        : cta_text_color) as string,
                      border: (hovered
                        ? cta_border_hover
                        : cta_border) as string,
                    }}
                    tabIndex={0}
                  >
                    {category?.cta_text ?? 'Shop Now'}
                  </span>
                )}
              </>
            );
            return (
              <Link
                aria-label={`Go to ${category?.category_name ?? 'category'}`}
                className={styles.categories_grid__container__categories}
                href={category?.cta_link ?? '/'}
                key={category.category_name}
                style={{ maxWidth: getItemMaxWidth() }}
              >
                {cardContent}
              </Link>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export { CategoriesCarousel, CategoriesGrid };
